import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../client/components/ui/dialog";
import { Button } from "../client/components/ui/button";
import { Input } from "../client/components/ui/input";
import { Label } from "../client/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../client/components/ui/select";
import { Checkbox } from "../client/components/ui/checkbox";
import { Badge } from "../client/components/ui/badge";
import { X } from "lucide-react";
import type { TradeTag, Account, TradeCustomFieldDefinition, TradingStrategy } from "wasp/entities";
import useTranslation from "../client/hooks/useTranslation";
import StrategyMultiSelect from "../client/components/StrategyMultiSelect";

export interface AdvancedFilters {
  accountId: string;
  groupId: string;
  strategyIds: string[];
  status: "all" | "open" | "closed";
  tagIds: string[];
  minPnl: string;
  maxPnl: string;
  customFields: Record<string, { value: any; operator?: string }>;
}

interface AdvancedFiltersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: AdvancedFilters;
  onFiltersChange: (filters: AdvancedFilters) => void;
  onApply: () => void;
  onReset: () => void;
  tags: TradeTag[];
  accounts: Account[];
  strategies: TradingStrategy[];
  customFields: TradeCustomFieldDefinition[];
  customFieldTags: Record<string, string[]>; // fieldId -> tags[]
  /** When true, hide controls redundant in the journal-widget context: account/group + strategy (handled by topbar) and custom fields (unsupported by the query). */
  hideAccountAndCustomFields?: boolean;
}

export default function AdvancedFiltersDialog({
  open,
  onOpenChange,
  filters,
  onFiltersChange,
  onApply,
  onReset,
  tags,
  accounts,
  strategies,
  customFields,
  customFieldTags,
  hideAccountAndCustomFields = false,
}: AdvancedFiltersDialogProps) {
  const t = useTranslation();

  const handleTagToggle = (tagId: string) => {
    const newTagIds = filters.tagIds.includes(tagId)
      ? filters.tagIds.filter((id) => id !== tagId)
      : [...filters.tagIds, tagId];
    onFiltersChange({ ...filters, tagIds: newTagIds });
  };

  const handleCustomFieldChange = (fieldId: string, value: any, operator?: string) => {
    const newCustomFields = { ...filters.customFields };
    if (value === "" || value === null || value === undefined) {
      delete newCustomFields[fieldId];
    } else {
      newCustomFields[fieldId] = { value, operator };
    }
    onFiltersChange({ ...filters, customFields: newCustomFields });
  };

  const getCustomFieldValue = (fieldId: string) => {
    return filters.customFields[fieldId]?.value || "";
  };

  const getCustomFieldOperator = (fieldId: string) => {
    return filters.customFields[fieldId]?.operator || "equals";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t.journal.advancedFiltersTitle}</DialogTitle>
          <DialogDescription>
            {t.journal.advancedFiltersDescription}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Account Filter — hidden in journal-widget context (filtering is handled by topbar) */}
          {!hideAccountAndCustomFields && (
            <div>
              <Label htmlFor="account">{t.journal.account}</Label>
              <Select
                value={filters.accountId || "all"}
                onValueChange={(value) =>
                  onFiltersChange({
                    ...filters,
                    accountId: value === "all" ? "" : value,
                  })
                }
              >
                <SelectTrigger id="account">
                  <SelectValue placeholder={t.journal.allAccounts} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t.journal.allAccounts}</SelectItem>
                  {accounts.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      {account.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Strategy Filter — hidden in journal-widget context (handled by topbar strategy filter) */}
          {!hideAccountAndCustomFields && strategies.length > 0 && (
            <div>
              <Label>{t.journal.strategy}</Label>
              <StrategyMultiSelect
                strategies={strategies}
                selectedIds={filters.strategyIds}
                onChange={(ids) => onFiltersChange({ ...filters, strategyIds: ids })}
                className="w-full mt-1.5"
              />
            </div>
          )}

          {/* Status Filter */}
          <div>
            <Label htmlFor="status">{t.journal.status}</Label>
            <Select
              value={filters.status}
              onValueChange={(value) =>
                onFiltersChange({
                  ...filters,
                  status: value as "all" | "open" | "closed",
                })
              }
            >
              <SelectTrigger id="status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.journal.all}</SelectItem>
                <SelectItem value="open">{t.journal.open}</SelectItem>
                <SelectItem value="closed">{t.journal.closed}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Tags Filter */}
          <div>
            <Label>{t.journal.tags}</Label>
            <div className="mt-2 space-y-2 max-h-48 overflow-y-auto border rounded-md p-3">
              {tags.length === 0 && Object.keys(customFieldTags).length === 0 ? (
                <p className="text-sm text-muted-foreground">{t.journal.noTagsAvailable}</p>
              ) : (
                <>
                  {/* Trade Tags (globaux) */}
                  {tags.length > 0 && (
                    <div className="space-y-2">
                      {tags.map((tag) => (
                        <div key={tag.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`tag-${tag.id}`}
                            checked={filters.tagIds.includes(tag.id)}
                            onCheckedChange={() => handleTagToggle(tag.id)}
                          />
                          <Label
                            htmlFor={`tag-${tag.id}`}
                            className="text-sm font-normal cursor-pointer flex-1"
                          >
                            {tag.name}
                          </Label>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Tags des champs custom */}
                  {Object.entries(customFieldTags).map(([fieldId, fieldTags]) => {
                    const field = customFields.find((f) => f.id === fieldId);
                    if (!field || fieldTags.length === 0) return null;
                    
                    return (
                      <div key={fieldId} className="space-y-2 pt-2 border-t">
                        <div className="text-xs font-medium text-muted-foreground mb-1">
                          {field.label}
                        </div>
                        {fieldTags.map((tagName) => {
                          const tagKey = `custom-${fieldId}-${tagName}`;
                          const isSelected = filters.customFields[fieldId]?.value?.includes(tagName);
                          
                          return (
                            <div key={tagKey} className="flex items-center space-x-2">
                              <Checkbox
                                id={tagKey}
                                checked={!!isSelected}
                                onCheckedChange={() => {
                                  const currentValue = filters.customFields[fieldId]?.value || "";
                                  const currentTags = currentValue ? currentValue.split(",").map((t: string) => t.trim()) : [];
                                  
                                  let newTags: string[];
                                  if (isSelected) {
                                    newTags = currentTags.filter((t: string) => t.toLowerCase() !== tagName.toLowerCase());
                                  } else {
                                    newTags = [...currentTags, tagName];
                                  }
                                  
                                  handleCustomFieldChange(fieldId, newTags.join(","));
                                }}
                              />
                              <Label
                                htmlFor={tagKey}
                                className="text-sm font-normal cursor-pointer flex-1"
                              >
                                {tagName}
                              </Label>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
            {filters.tagIds.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {tags
                  .filter((tag) => filters.tagIds.includes(tag.id))
                  .map((tag) => (
                    <Badge
                      key={tag.id}
                      variant="secondary"
                      className="flex items-center gap-1"
                    >
                      {tag.name}
                      <button
                        onClick={() => handleTagToggle(tag.id)}
                        className="ml-1 hover:bg-secondary-foreground/20 rounded-full p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
              </div>
            )}
          </div>

          {/* PnL Range Filter */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="minPnl">{t.journal.minPnl}</Label>
              <Input
                id="minPnl"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={filters.minPnl}
                onChange={(e) =>
                  onFiltersChange({ ...filters, minPnl: e.target.value })
                }
              />
            </div>
            <div>
              <Label htmlFor="maxPnl">{t.journal.maxPnl}</Label>
              <Input
                id="maxPnl"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={filters.maxPnl}
                onChange={(e) =>
                  onFiltersChange({ ...filters, maxPnl: e.target.value })
                }
              />
            </div>
          </div>

          {/* Custom Fields Filters — hidden in journal-widget context (not supported by the widget query) */}
          {!hideAccountAndCustomFields && customFields.length > 0 && (
            <div>
              <Label className="mb-3 block">{t.journal.customFields}</Label>
              <div className="space-y-4">
                {customFields.map((field) => {
                  const fieldValue = getCustomFieldValue(field.id);
                  const operator = getCustomFieldOperator(field.id);

                  return (
                    <div key={field.id} className="space-y-2">
                      <Label className="text-sm font-medium">{field.label}</Label>
                      <div className="flex gap-2">
                        {field.type === "number" && (
                          <Select
                            value={operator}
                            onValueChange={(value) =>
                              handleCustomFieldChange(field.id, fieldValue, value)
                            }
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="equals">{t.journal.equals}</SelectItem>
                              <SelectItem value="greater">{t.journal.greater}</SelectItem>
                              <SelectItem value="less">{t.journal.less}</SelectItem>
                              <SelectItem value="between">{t.journal.between}</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                        {field.type === "text" && (
                          <Select
                            value={operator}
                            onValueChange={(value) =>
                              handleCustomFieldChange(field.id, fieldValue, value)
                            }
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="equals">{t.journal.equals}</SelectItem>
                              <SelectItem value="contains">{t.journal.contains}</SelectItem>
                              <SelectItem value="startsWith">{t.journal.startsWith}</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                        {field.type === "date" && (
                          <Select
                            value={operator}
                            onValueChange={(value) =>
                              handleCustomFieldChange(field.id, fieldValue, value)
                            }
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="equals">{t.journal.equals}</SelectItem>
                              <SelectItem value="after">{t.journal.after}</SelectItem>
                              <SelectItem value="before">{t.journal.before}</SelectItem>
                              <SelectItem value="between">{t.journal.between}</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                        {field.type === "boolean" && (
                          <Select
                            value={fieldValue === "" ? "" : fieldValue ? "true" : "false"}
                            onValueChange={(value) =>
                              handleCustomFieldChange(
                                field.id,
                                value === "" ? "" : value === "true",
                              )
                            }
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue placeholder={t.journal.all} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="">{t.journal.all}</SelectItem>
                              <SelectItem value="true">{t.journal.yes}</SelectItem>
                              <SelectItem value="false">{t.journal.no}</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                        {field.type === "tags" && (
                          <Input
                            placeholder={t.journal.tagsSeparatedByCommas}
                            value={fieldValue}
                            onChange={(e) =>
                              handleCustomFieldChange(field.id, e.target.value)
                            }
                            className="flex-1"
                          />
                        )}
                        {field.type !== "boolean" && field.type !== "tags" && (
                          <Input
                            type={
                              field.type === "number"
                                ? "number"
                                : field.type === "date"
                                  ? "date"
                                  : "text"
                            }
                            step={field.type === "number" ? "0.01" : undefined}
                            placeholder={t.journal.value}
                            value={fieldValue}
                            onChange={(e) =>
                              handleCustomFieldChange(field.id, e.target.value)
                            }
                            className="flex-1"
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onReset}>
            {t.journal.reset}
          </Button>
          <Button onClick={onApply}>{t.journal.applyFilters}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

